import React, { useState } from "react";
import axiosInstance from '../utils/axiosConfig';
import config from '../utils/config';
import { getActionErrorMessage } from '../utils/errorHandler';
import clinExpertLogo from "../ClinExpert_logo.png";
import "../styles/LoginForm.css";
import { FaEnvelope, FaCheck, FaTimes } from "react-icons/fa";
import AuthBackButton from "./AuthBackButton";

const ForgotPasswordForm = () => {
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isEmailValid, setIsEmailValid] = useState(false);
  const [isEmailTouched, setIsEmailTouched] = useState(false);

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleEmailChange = (e) => {
    const value = e.target.value;
    setEmail(value);
    setIsEmailValid(validateEmail(value));
    setIsEmailTouched(true);
    setError("");
  };

  const handleRequestReset = async (event) => {
    event.preventDefault();
    setError("");
    setSuccess("");
    
    if (!isEmailValid) {
      setError("Please enter a valid email address.");
      return;
    }

    setIsLoading(true);

    try {
      const response = await axiosInstance.post("/auth/request-password-reset", {
        email: email
      }, {
        timeout: config.AUTH_TIMEOUT, // Extended timeout for Render cold starts
      });

      if (response.data.status === "success") {
        setSuccess(response.data.message);
        setEmail(""); // Clear the email field
        setIsEmailValid(false);
        setIsEmailTouched(false);
      }
    } catch (err) {
      console.error(err);
      // Use sanitized error message - never show raw backend errors
      setError(getActionErrorMessage(err, "send password reset email"));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="login-container">
      <AuthBackButton />
      <div className="login-shell forgot-shell">
        <section className="login-brand-panel forgot-brand-panel">
          <img src={clinExpertLogo} alt="ClinExpert Logo" className="logo dual-logo" />
          <h1 className="login-title">ClinExpert Portal</h1>
          <p className="login-subtitle">
            Recover access to your secure academic workspace in a few steps.
          </p>
          <div className="login-feature-list">
            <div className="login-feature-item">
              <FaEnvelope />
              <span>Reset instructions sent securely</span>
            </div>
            <div className="login-feature-item">
              <FaCheck />
              <span>Fast, clean, and institution-ready</span>
            </div>
          </div>
          <p className="login-brand-footer">Brought to you by ClinExpert Inc</p>
        </section>

        <section className="login-form-panel forgot-form-panel content-box">
          <div className="forgot-header">
            <h1 className="forgot-title">Forgot Password?</h1>
            <p className="forgot-subtitle">
              No worries. Enter your email and we’ll send reset instructions.
            </p>
          </div>

          <form onSubmit={handleRequestReset}>
            <div style={{ marginBottom: '1.4rem' }}>
              <label className="form-label">
                Email Address
              </label>
              <div style={{ position: 'relative', display: 'flex', flexDirection: 'column' }}>
                <div className="input-group" style={{ marginBottom: 0 }}>
                  <span className="input-icon">
                    <FaEnvelope />
                  </span>
                  <input
                    type="email"
                    value={email}
                    onChange={handleEmailChange}
                    onBlur={() => setIsEmailTouched(true)}
                    placeholder="Enter your email address"
                    required
                    style={{
                      borderColor: isEmailTouched && !isEmailValid ? '#dc2626' : 
                                  isEmailValid ? '#10b981' : '#c2d2e5',
                      background: isEmailTouched && !isEmailValid ? '#fef2f2' : 
                                 isEmailValid ? '#f0fdf4' : '#ffffff',
                      paddingRight: isEmailTouched ? '2.7rem' : '2.7rem'
                    }}
                  />
                  {isEmailTouched && (
                    <span className="validation-icon">
                      {isEmailValid ? (
                        <FaCheck style={{ color: '#10b981', fontSize: '1.05rem' }} />
                      ) : (
                        <FaTimes style={{ color: '#dc2626', fontSize: '1.05rem' }} />
                      )}
                    </span>
                  )}
                </div>
                {isEmailTouched && !isEmailValid && (
                  <p className="forgot-validation-error">
                    Please enter a valid email address
                  </p>
                )}
              </div>
            </div>
            
            <button 
              type="submit" 
              className="login-button"
              disabled={isLoading || !isEmailValid}
              style={{
                opacity: isLoading || !isEmailValid ? 0.65 : 1,
                cursor: isLoading || !isEmailValid ? 'not-allowed' : 'pointer'
              }}
            >
              {isLoading ? "Sending..." : "Send Reset Link"}
            </button>
            
            {error && (
              <div className="login-inline-status" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <FaTimes style={{ flexShrink: 0 }} />
                <span>{error}</span>
              </div>
            )}
            {success && (
              <div className="forgot-success-pill" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', justifyContent: 'center' }}>
                <FaCheck style={{ flexShrink: 0 }} />
                <span>{success}</span>
              </div>
            )}
          </form>
        </section>
      </div>
    </div>
  );
};

export default ForgotPasswordForm; 